import { Dropdown } from '@/components';
import { VITE_APP_BASENAME } from '@/config';
import styles from '@/features/analysis/_components/analysis-table/AnalysisDrillThroughDropdown.module.scss';
import type { ProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { PROJECT_BASE_DIR } from '@/features/project/_utils/constants';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { initialValueOfProjectSearchForm } from '@/features/search/_models/projects-search';
import { ExportOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';

const dropdownClass = classNames(styles.dropdown_icon);

/**
 * 基金シート分析画面の検索条件から事業一覧ページ用の検索クエリを組み立てる純粋関数。
 *
 * 事業所管課室は内部サイトでは `cascader`（QueryStore ID）、公開サイト（PS）では
 * `organizationIds`（leaf id 配列）で表現される。`organizationIds` が存在する場合
 * （= PS）はそれを真実とし、`cascader` を null にして競合を避ける（RS-4474）。
 */
export const buildKsDrillThroughProjectParams = (
  fiscalYear: string,
  searchQueryStates: FundSheetAnalysisPageSearchQueryStates,
  initialValue: ReturnType<typeof initialValueOfProjectSearchForm>,
): ProjectsPageSearchQueryStates => {
  // PS では organizationIds(leaf id) を真実とし cascader は引き継がない。
  // 内部サイトでは organizationIds が null のため従来どおり cascader を引き継ぐ。
  const hasOrganizationIds = !!searchQueryStates.organizationIds?.length;

  return {
    fiscalYear,
    sheetType: 'KS',
    fundSheetType: 'standard',
    q: searchQueryStates.q,
    isSearchTargetProjectName: searchQueryStates.isSearchTargetProjectName,
    cascader: hasOrganizationIds ? null : searchQueryStates.cascader,
    organizationIds: searchQueryStates.organizationIds,
    corporateNumber: searchQueryStates.corporateNumber,
    projectNumbers: searchQueryStates.projectNumbers,
    // 以下、RS-4400 のドリルスルー仕様には含まれない項目。null/初期値で渡す。
    categories: null,
    implementationMethods: null,
    accountingType: null,
    accountingTypeIds: null,
    startYear: null,
    endYear: null,
    otherResponsibleOrganizationIds: null,
    policyIds: null,
    expenditureBudgetPurposeIds: null,
    page: 1,
    pageSize: 30,
    skipAdministrativeReformTeamEvaluation:
      initialValue.skipAdministrativeReformTeamEvaluation,
    isPublished: null,
    inspectionByExternalExpertReviewType: null,
    isCandidateOfInspectionByExternalExpert: null,
    formulaConditions: null,
    ordering: null,
    lastImplementedFiscalYearRangeMax: null,
    lastImplementedFiscalYearRangeMin: null,
    indicatorKeyword: null,
    indicatorKeywordSearchOnlyTitleAndDescription: false,
    indicatorType: null,
    indicatorTerm: null,
    indicatorDataNatureType: null,
    hasOnlyQualitativeIndicator: false,
    groupInProjectsOrdering: null,
    groupInProjectsPage: 1,
  };
};

type Props = {
  fiscalYears?: number[];
  disabled?: boolean;
  fallbackText?: string;
};

/**
 * 基金シート分析画面から事業一覧へドリルスルーするドロップダウン。
 *
 * 引き継ぐ検索項目（RS-4400 / RS-4474）:
 * - 年度 / キーワード / 事業所管課室（内部サイト=cascader, 公開サイト=organizationIds） /
 *   支出先名（corporateNumber） / 予算事業ID（projectNumbers）
 *
 * sheetType / fundSheetType は基金シート分析画面固定値 ('KS' / 'standard') を渡す。
 */
export const KsAnalysisDrillThroughDropdown: FC<Props> = ({
  fiscalYears,
  disabled,
  fallbackText,
}) => {
  const { t } = useTranslation();
  const { searchQueryStates } = useFundSheetAnalysisPageSearchQueryStates();
  const currentFiscalYear = useCurrentFiscalYear();
  const initialValue = initialValueOfProjectSearchForm(
    currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
  );
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(
    fiscalYears?.at(-1)?.toString(),
  );

  const handleDrillThrough = (fiscalYear: string) => {
    const projectSearchParams = buildKsDrillThroughProjectParams(
      fiscalYear,
      searchQueryStates,
      initialValue,
    );

    window.open(
      `${VITE_APP_BASENAME}${PROJECT_BASE_DIR}?${new URLSearchParams(
        removeNullish(projectSearchParams) as Record<string, string>,
      )}`,
      '_blank',
    );
  };

  return (
    <Dropdown.Button
      menu={{
        items: [...(fiscalYears ?? [])]
          .sort()
          .reverse()
          .map((fiscalYear) => ({
            key: fiscalYear,
            label: fiscalYear,
          })),
        onClick: (e) => {
          setSelectedFiscalYear(e.key);
        },
      }}
      className={dropdownClass}
      icon={selectedFiscalYear ?? fallbackText}
      onClick={() => {
        if (selectedFiscalYear) {
          handleDrillThrough(selectedFiscalYear);
        }
      }}
      disabled={disabled}
    >
      <ExportOutlined />
      {t('drill-through')}
    </Dropdown.Button>
  );
};

const removeNullish = <T extends Record<string, unknown>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined),
  );
